"use client";

import React, { useState } from 'react';
import Link from 'next/link';

interface Pillar {
  id: string;
  name: string;
  pct: number;
  current: string;
  target: string;
  indicators: number;
  source: string;
  trend: 'up' | 'stable' | 'down';
  href: string;
}

const pillars: Pillar[] = [
  { id: 'economy', name: 'Economy', pct: 8.6, current: '$260B GDP', target: '$3 Trillion GDP', indicators: 8, source: 'RBI / MoSPI', trend: 'up', href: '/economy' },
  { id: 'innovation', name: 'Innovation', pct: 18.5, current: '1,847 Startups', target: '10,000 Startups', indicators: 6, source: 'DPIIT / T-Hub', trend: 'up', href: '/homepage' },
  { id: 'manufacturing', name: 'Manufacturing', pct: 49.6, current: '12.4% GSDP', target: '25% GSDP', indicators: 5, source: 'GoTS / DIPP', trend: 'up', href: '/homepage' },
  { id: 'agriculture', name: 'Agriculture', pct: 32.0, current: '₹32K/acre/yr', target: '₹1L/acre/yr', indicators: 7, source: 'GoTS / APEDA', trend: 'stable', href: '/homepage' },
  { id: 'infrastructure', name: 'Infrastructure', pct: 34.0, current: '34% complete', target: '100% Tier-2 Connectivity', indicators: 6, source: 'MoRTH / GoTS', trend: 'up', href: '/homepage' },
  { id: 'energy', name: 'Energy', pct: 37.3, current: '11.2 GW RE', target: '30 GW Renewable', indicators: 5, source: 'MNRE / TSECL', trend: 'up', href: '/homepage' },
  { id: 'education', name: 'Education', pct: 41.2, current: '78% literacy', target: '98% literacy', indicators: 6, source: 'Census / ASER', trend: 'up', href: '/homepage' },
  { id: 'healthcare', name: 'Healthcare', pct: 28.7, current: '1.8 beds/1000', target: '5 beds/1000', indicators: 5, source: 'NHM / GoTS', trend: 'stable', href: '/homepage' },
  { id: 'environment', name: 'Environment', pct: 22.4, current: '24% forest cover', target: '33% forest cover', indicators: 4, source: 'FSI / MoEF', trend: 'down', href: '/homepage' },
  { id: 'governance', name: 'Governance', pct: 31.5, current: '62/100 index', target: '95/100 index', indicators: 5, source: 'PRS / GoTS', trend: 'up', href: '/homepage' },
];

function getBarClass(pct: number) {
  if (pct < 20) return 'low';
  if (pct < 40) return 'mid';
  return 'high';
}

function getStatusLabel(pct: number) {
  if (pct < 20) return { label: 'Critical', cls: 'text-danger bg-red-50 border-red-100' };
  if (pct < 40) return { label: 'Moderate', cls: 'text-warning bg-amber-50 border-amber-100' };
  return { label: 'On Track', cls: 'text-success bg-green-50 border-green-100' };
}

function TrendIcon({ trend }: { trend: 'up' | 'stable' | 'down' }) {
  if (trend === 'up') return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-success">
      <path d="M2 10L6 6L9 8L12 4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  if (trend === 'down') return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-danger">
      <path d="M2 4L6 8L9 6L12 10" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  return (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="text-muted">
      <path d="M2 7H12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

export default function TrackerDashboard() {
  const [filter, setFilter] = useState<'all' | 'critical' | 'moderate' | 'ontrack'>('all');
  const [view, setView] = useState<'bars' | 'table'>('bars');

  const filtered = pillars.filter((p) => {
    if (filter === 'critical') return p.pct < 20;
    if (filter === 'moderate') return p.pct >= 20 && p.pct < 40;
    if (filter === 'ontrack') return p.pct >= 40;
    return true;
  });

  const avgProgress = Math.round(pillars.reduce((a, b) => a + b.pct, 0) / pillars.length);

  return (
    <section className="section-padding bg-research">
      <div className="container-research">
        {/* Summary Row */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Avg. Progress', value: `${avgProgress}%`, note: 'Across 10 pillars' },
            { label: 'Critical Pillars', value: pillars.filter(p => p.pct < 20).length.toString(), note: '< 20% progress' },
            { label: 'Moderate Pillars', value: pillars.filter(p => p.pct >= 20 && p.pct < 40).length.toString(), note: '20–40% progress' },
            { label: 'On Track Pillars', value: pillars.filter(p => p.pct >= 40).length.toString(), note: '> 40% progress' },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-border rounded p-4">
              <p className="text-2xl font-mono font-bold text-primary">{s.value}</p>
              <p className="text-sm font-medium text-foreground">{s.label}</p>
              <p className="text-xs text-muted">{s.note}</p>
            </div>
          ))}
        </div>

        {/* Controls */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-2 flex-wrap">
            {([
              { key: 'all', label: 'All Pillars' },
              { key: 'critical', label: '🔴 Critical' },
              { key: 'moderate', label: '🟡 Moderate' },
              { key: 'ontrack', label: '🟢 On Track' },
            ] as const).map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded border transition-colors ${
                  filter === f.key
                    ? 'bg-primary text-white border-primary' :'bg-white text-foreground border-border hover:border-accent'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setView('bars')}
              className={`px-3 py-1.5 text-xs font-medium rounded border transition-colors ${view === 'bars' ? 'bg-primary text-white border-primary' : 'bg-white text-foreground border-border hover:border-accent'}`}
            >
              Progress View
            </button>
            <button
              onClick={() => setView('table')}
              className={`px-3 py-1.5 text-xs font-medium rounded border transition-colors ${view === 'table' ? 'bg-primary text-white border-primary' : 'bg-white text-foreground border-border hover:border-accent'}`}
            >
              Table View
            </button>
          </div>
        </div>

        {/* Progress Bars View */}
        {view === 'bars' && (
          <div className="bg-white border border-border rounded shadow-card">
            <div className="px-6 py-4 border-b border-border bg-research rounded-t flex items-center justify-between">
              <span className="text-xs font-mono font-semibold text-primary uppercase tracking-wider">
                Progress to Vision 2047 Targets
              </span>
              <span className="text-xs text-muted font-mono">Updated: March 2026</span>
            </div>
            <div className="p-6 space-y-5">
              {filtered.map((pillar) => {
                const status = getStatusLabel(pillar.pct);
                return (
                  <div key={pillar.id} className="group">
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2">
                        <Link href={pillar.href} className="text-sm font-semibold text-primary hover:text-accent transition-colors w-32">
                          {pillar.name}
                        </Link>
                        <span className={`pillar-badge border text-[10px] ${status.cls}`}>{status.label}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <TrendIcon trend={pillar.trend} />
                        <span className="text-xs font-mono text-muted hidden sm:block">{pillar.current}</span>
                        <span className="text-sm font-mono font-bold text-primary w-12 text-right">{pillar.pct}%</span>
                      </div>
                    </div>
                    <div className="progress-bar-track">
                      <div
                        className={`progress-bar-fill ${getBarClass(pillar.pct)}`}
                        style={{ width: `${pillar.pct}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between mt-1">
                      <span className="text-[11px] text-muted font-mono">Target: {pillar.target}</span>
                      <span className="text-[11px] text-muted font-mono">{pillar.indicators} indicators · {pillar.source}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Table View */}
        {view === 'table' && (
          <div className="bg-white border border-border rounded shadow-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="research-table">
                <thead>
                  <tr>
                    <th>Pillar</th>
                    <th>Current Value</th>
                    <th>2047 Target</th>
                    <th>Progress</th>
                    <th>Status</th>
                    <th>Trend</th>
                    <th>Indicators</th>
                    <th>Source</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((pillar) => {
                    const status = getStatusLabel(pillar.pct);
                    return (
                      <tr key={pillar.id}>
                        <td>
                          <Link href={pillar.href} className="font-semibold text-primary hover:text-accent transition-colors">
                            {pillar.name}
                          </Link>
                        </td>
                        <td className="font-mono text-sm">{pillar.current}</td>
                        <td className="font-mono text-sm text-muted">{pillar.target}</td>
                        <td>
                          <div className="flex items-center gap-2">
                            <div className="w-20 progress-bar-track">
                              <div className={`progress-bar-fill ${getBarClass(pillar.pct)}`} style={{ width: `${pillar.pct}%` }} />
                            </div>
                            <span className="font-mono text-sm font-semibold">{pillar.pct}%</span>
                          </div>
                        </td>
                        <td>
                          <span className={`pillar-badge border text-[10px] ${status.cls}`}>{status.label}</span>
                        </td>
                        <td><TrendIcon trend={pillar.trend} /></td>
                        <td className="font-mono text-sm text-muted">{pillar.indicators}</td>
                        <td className="text-xs text-muted">{pillar.source}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <p className="text-xs text-muted font-mono mt-4">
          * All progress values are estimates based on publicly available data. Figures subject to revision upon official data releases. See Methodology for calculation details.
        </p>
      </div>
    </section>
  );
}